import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import * as AuthActions from './auth.actions';
import {
  SharedLibraryService,
  ConfirmResetService,
} from 'projects/petszel-shared/src/public-api';
import { AuthGuard } from '../../service/guards/authentication-guard.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from "@ngrx/store";
import { AuthState } from "./auth.state";
import { PermissionsHandlerService } from "../../service/permissions-handler.service";
import { resetPermissions } from "../user/permissions/permissions.actions";
import {
  loadFeatureFlagAdopterHub,
  resetFeatureFlagAdopterHub
} from "../user/feature-flag-adopter-hub/feature-flag-adopter-hub.actions";
import { resetShelterSites } from "../shelter/sites/shelter-sites.actions";

interface LoginResponse {
  isSuccess: boolean;
  token: string;
  expiration: string;
}

interface DecodedToken {
  'petszel-credentialtype': string | string[];
  'petszel-userid': string;
  email: string;
  [key: string]: any;
}

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private router: Router,
    private logger: NGXLogger,
    private sharedLibraryService: SharedLibraryService,
    private authGuard: AuthGuard,
    private confirmResetService: ConfirmResetService,
    private modalService: NgbModal,
    private store: Store<AuthState>,
    private permissionsHandlerService: PermissionsHandlerService
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ username, password, applicationId }) => {
        const url =
          this.sharedLibraryService.getSharedLibraryConfig().apiUrl +
          'Authorize';
        const data = { username, password, applicationId, credentialType: '' };

        return this.http.post<LoginResponse>(url, data).pipe(
          map((response) => {
            if (
              response.isSuccess &&
              response.token &&
              new Date(response.expiration) > new Date()
            ) {
              try {
                const decodedToken = jwtDecode<DecodedToken>(response.token);

                let credentials = decodedToken['petszel-credentialtype'];
                if (!Array.isArray(credentials)) {
                  credentials = [credentials];
                }

                const userId = decodedToken['petszel-userid'] || '';
                const userEmail = decodedToken['email'] || username;

                // Define roles in order of priority
                const rolePriority = [
                  'PetszelAdmin',
                  'PetszelStaff',
                  'PetszelConnectShelterAdmin',
                  'PetszelConnectShelterSiteAdmin',
                  'PetszelConnectShelterStaff',
                  'PetszelConnectShelterManager',
                  'PetszelConnectShelterMedicalStaff',
                  'PetszelConnectShelterBehaviorStaff',
                  'PetszelConnectShelterMarketingStaff',
                  'PetszelConnectShelterCustomerServiceStaff',
                  'PetszelConnectShelterViewOnlyStaff',
                ];

                const matchedRole = rolePriority.find((role) =>
                  credentials.includes(role),
                );

                if (matchedRole) {
                  this.logger.log('User login successful');
                  return AuthActions.loginSuccess({
                    token: response.token,
                    userId,
                    userEmail,
                    userRoles: credentials as string[],
                  });
                } else {
                  return AuthActions.loginFailure({
                    error: 'Invalid credentials',
                  });
                }
              } catch (error) {
                return AuthActions.loginFailure({ error: 'Invalid token' });
              }
            } else {
              return AuthActions.loginFailure({ error: 'Invalid credentials' });
            }
          }),
          catchError((error) => {
            this.logger.error(error, 'Login error occurred');

            if (error.status === 400 || error.status === 401) {
              return of(
                AuthActions.loginFailure({ error: 'Invalid credentials' }),
              );
            } else {
              return of(AuthActions.loginFailure({ error: 'Network error' }));
            }
          }),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, userRoles }) => {
          // Persist to localStorage for session management
          localStorage.setItem('token', token);

          this.permissionsHandlerService.manageUserPermissionsAndScopes();
          this.store.dispatch(loadFeatureFlagAdopterHub());

          // Redirect using existing auth guard
          const matchedRole = userRoles[0];
          if (matchedRole) {
            this.authGuard.redirectCallback(matchedRole);
          }
        }),
      ),
    { dispatch: false },
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(() => {
          localStorage.removeItem('token');
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        localStorage.removeItem('token');
        this.store.dispatch(AuthActions.resetLogin());
        this.store.dispatch(resetPermissions());
        this.store.dispatch(resetShelterSites());
        this.store.dispatch(resetFeatureFlagAdopterHub())
        this.modalService.dismissAll();
        this.router.navigate(['/']);
      }),
      map(() => AuthActions.logoutSuccess()),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      switchMap(({ email }) => {
        return this.confirmResetService
          .postResetPassword({ username: email })
          .pipe(
            map(() => {
              this.logger.log('Reset password email sent successfully');
              return AuthActions.resetPasswordSuccess();
            }),
            catchError((error) => {
              this.logger.error(error, 'Reset password error occurred');
              return of(
                AuthActions.resetPasswordFailure({
                  error:
                    'Unable to send reset email. Please try again shortly.',
                }),
              );
            }),
          );
      }),
    ),
  );

  resetPasswordSuccess$ = createEffect(
    () => this.actions$.pipe(ofType(AuthActions.resetPasswordSuccess)),
    { dispatch: false },
  );

  hydrateAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hydrateAuth, AuthActions.loadAuthFromStorage),
      map(() => {
        const token = localStorage.getItem('token');

        if (!token) {
          return AuthActions.hydrateAuthFailure({ error: 'Token not found' });
        }

        try {
          const decodedToken = jwtDecode<DecodedToken>(token);

          if (decodedToken['exp'] && decodedToken['exp'] * 1000 <= Date.now()) {
            localStorage.removeItem('token');
            return AuthActions.hydrateAuthFailure({ error: 'Session expired' });
          }

          let credentials = decodedToken['petszel-credentialtype'] || [];
          if (!Array.isArray(credentials)) {
            credentials = [credentials];
          }

          const userId = decodedToken['petszel-userid'] || '';
          const userEmail = decodedToken['email'] || '';

          return AuthActions.hydrateAuthSuccess({
            token,
            userId,
            userEmail,
            userRoles: credentials as string[],
          });
        } catch {
          localStorage.removeItem('token');
          return AuthActions.hydrateAuthFailure({ error: 'Invalid token' });
        }
      }),
    ),
  );

  hydrateAuthSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.hydrateAuthSuccess),
        tap(() => {
          this.permissionsHandlerService.manageUserPermissionsAndScopes();
          this.store.dispatch(loadFeatureFlagAdopterHub());
        }),
      ),
    { dispatch: false },
  );

  hydrateAuthFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.hydrateAuthFailure),
        tap(() => {
          this.store.dispatch(AuthActions.resetLogin());
        }),
      ),
    { dispatch: false },
  );
}
